import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AlertBanner from './AlertBanner';
import ReportEditor from './report-editor/ReportEditor';
import AdminWidgetBuilder from './AdminWidgetBuilder';
import WidgetLibrary from './dashboard-editor/WidgetLibrary';
import WidgetInspector from './dashboard-editor/WidgetInspector';
import DashboardSettingsPanel from './dashboard-editor/DashboardSettingsPanel';
import NewDashboardDialog from './dashboard-editor/NewDashboardDialog';
import useDashboardEditor from './dashboard-editor/useDashboardEditor';
import { GRID_BREAKPOINTS, layoutItemsFor, widgetLabel, rowHeightFor, widescreenSlots } from './dashboard-editor/editorUtils';
import ContinuousWall from './ContinuousWall';
import DashboardFilterBar from './DashboardFilterBar';
import '../styles/DashboardEditor.css';
import { loadDashboardData } from '../utils/loadDashboardData';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import WidgetFactory from './widgets/WidgetFactory';
import useStore from '../store/useStore';
import useAssistant from '../store/useAssistant';
import AiMark from './assistant/AiMark';
import { dashboardsAPI } from '../services/api';
import { useDashboardStale } from '../hooks/useDashboardStale';
import useAutoScroll from '../hooks/useAutoScroll';

const CONTROLS_FADE_MS = 5000;
const ROTATE_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 30, label: 'Every 30 seconds' },
  { value: 60, label: 'Every minute' },
  { value: 120, label: 'Every 2 minutes' },
  { value: 300, label: 'Every 5 minutes' },
];

/** The choices for moving between dashboards, including any other time set by a wall URL. */
function rotateOptions(seconds) {
  if (ROTATE_OPTIONS.some((option) => option.value === seconds)) return ROTATE_OPTIONS;
  return [...ROTATE_OPTIONS, { value: seconds, label: `Every ${seconds} seconds` }];
}

const SCROLL_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'this', label: 'This dashboard' },
  { value: 'across', label: 'Across dashboards' },
];

/** The browser's remembered scroll choice. An earlier version stored only on or off. */
function storedScrollMode() {
  try {
    const stored = localStorage.getItem('wallScrollMode');
    if (SCROLL_OPTIONS.some((option) => option.value === stored)) return stored;
    return localStorage.getItem('wallAutoScroll') === '1' ? 'this' : 'off';
  } catch {
    return 'off';
  }
}

function storedDashboardIds() {
  try {
    const stored = JSON.parse(localStorage.getItem('wallDashboardIds') || '[]');
    return Array.isArray(stored) ? stored.filter(Number.isInteger) : [];
  } catch {
    return [];
  }
}

const Dashboard = () => {
  const navigate = useNavigate();

  // Zustand selectors
  const currentDashboard = useStore((state) => state.currentDashboard);
  const user = useStore((state) => state.user);
  const isEditMode = useStore((state) => state.isEditMode);
  const setEditMode = useStore((state) => state.setEditMode);
  const updateDashboard = useStore((state) => state.updateDashboard);
  const dataReady = useStore((state) => state.dataReady);
  const setDashboardDateRange = useStore((state) => state.setDashboardDateRange);
  const setWallMode = useStore((state) => state.setWallMode);
  const isStale = useDashboardStale();

  // Helper to determine initial breakpoint from window width
  const getInitialBreakpoint = () => {
    if (typeof window === 'undefined') return 'lg';
    const width = window.innerWidth;
    if (width >= 1920) return 'xl';
    if (width >= 1200) return 'lg';
    if (width >= 996) return 'md';
    if (width >= 768) return 'sm';
    return 'xs';
  };

  // Local state
  const [layouts, setLayouts] = useState({ xl: [], lg: [], md: [], sm: [], xs: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [currentBreakpoint, setCurrentBreakpoint] = useState(getInitialBreakpoint());
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [renderedDashboardId, setRenderedDashboardId] = useState(currentDashboard?.id);
  const [cachedWidgets, setCachedWidgets] = useState(currentDashboard?.widgets || []);
  // The period and filter bar shows when the dashboard has reports to apply them to, or filters set up.
  const showFilterBar = (currentDashboard?.widgets || []).some((w) => w.report) || (currentDashboard?.filters || []).length > 0;
  // Widgets never overlap; moving one pushes the others aside. "Snap up"
  // (vertical compaction) is optional and remembered per browser.
  const [snapUp, setSnapUp] = useState(() => {
    try { return localStorage.getItem('dashboardSnapUp') === '1'; } catch { return false; }
  });
  const gridOptions = { preventCollision: false, allowOverlap: false, compactType: snapUp ? 'vertical' : null };
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchParams, setUrlParams] = useSearchParams();
  const dashboards = useStore((state) => state.dashboards);
  // A wall URL says what it wants; otherwise this browser remembers the last choice.
  const [rotateSeconds, setRotateSeconds] = useState(() => {
    if (searchParams.get('wall') === '1') return Number(searchParams.get('rotate')) || 0;
    try { return Number(localStorage.getItem('wallRotateSeconds')) || 0; } catch { return 0; }
  });
  const [scrollMode, setScrollMode] = useState(() => {
    if (searchParams.get('wall') !== '1') return storedScrollMode();
    const asked = searchParams.get('scroll');
    return asked === 'continuous' ? 'across' : asked === '1' ? 'this' : 'off';
  });
  // The dashboards to move between. Empty means all of them, including any added later.
  const [chosenIds, setChosenIds] = useState(() => (
    searchParams.get('wall') === '1'
      ? (searchParams.get('dashboards') || '').split(',').map(Number).filter(Boolean)
      : storedDashboardIds()
  ));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [controlsIdle, setControlsIdle] = useState(false);
  const isCalculatingRef = useRef(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [showNewDashboard, setShowNewDashboard] = useState(false);
  const editor = useDashboardEditor(() => layoutsRef.current[currentBreakpointRef.current] || []);
  const layoutsRef = useRef(layouts);
  const currentBreakpointRef = useRef(currentBreakpoint);
  layoutsRef.current = layouts;
  currentBreakpointRef.current = currentBreakpoint;
  // The grid is as wide as its container (the page minus the sidebar), not the window.
  const [gridEl, setGridEl] = useState(null);
  const [gridWidth, setGridWidth] = useState(0);
  useEffect(() => {
    if (!gridEl) return undefined;
    const update = () => setGridWidth(gridEl.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(gridEl);
    return () => observer.disconnect();
  }, [gridEl]);
  const currentDashboardIdRef = useRef(currentDashboard?.id);

  // Calculate row height based on window width
  const getRowHeight = () => rowHeightFor(windowWidth);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fullscreen handlers
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error entering fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Error exiting fullscreen:', err);
      });
    }
  };

  // Listen for fullscreen changes (in case user exits with ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Dashboard date range follows the dashboard default; wall mode follows fullscreen
  useEffect(() => {
    setDashboardDateRange(currentDashboard?.default_date_range || null);
  }, [currentDashboard?.id, currentDashboard?.default_date_range, setDashboardDateRange]);

  // Wall mode: browser fullscreen, or a URL a TV can open without a click
  // (/?wall=1&rotate=60&scroll=1&dashboards=3,4). Sidebar and header hide, reports
  // render compact, and it can scroll each dashboard and move on to the next.
  // /?edit=library opens the editor with the widget library (used after creating a blank dashboard).
  useEffect(() => {
    if (searchParams.get('edit') === 'library' && isAdmin && currentDashboard) {
      setEditMode(true);
      editor.setPanel({ type: 'library' });
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setUrlParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentDashboard?.id]);

  const urlWall = searchParams.get('wall') === '1';
  const wallMode = isFullscreen || urlWall;
  const assistantAvailable = useAssistant((s) => Boolean(s.status?.available));
  const openAssistant = useAssistant((s) => s.openPanel);
  const chosen = dashboards.filter((d) => chosenIds.includes(d.id));
  const rotatePool = chosen.length ? chosen : dashboards;
  const hasChoice = dashboards.length > 1;
  const continuousActive = wallMode && scrollMode === 'across' && !isEditMode && rotatePool.length > 0;
  const rotating = !continuousActive && rotatePool.length > 1 && rotateSeconds >= 10;
  const nextDashboard = rotating
    ? rotatePool[(rotatePool.findIndex((d) => d.id === currentDashboard?.id) + 1) % rotatePool.length]
    : null;
  const scrollPassInProgress = useAutoScroll(wallMode && scrollMode === 'this' && !isEditMode, currentDashboard?.id);

  useEffect(() => {
    setWallMode(wallMode);
  }, [wallMode, setWallMode]);

  useEffect(() => () => setWallMode(false), [setWallMode]);

  // Leaving the dashboard ends editing; switching dashboards closes any open panel.
  useEffect(() => () => setEditMode(false), [setEditMode]);
  useEffect(() => { editor.setPanel(null); useStore.getState().clearWidgetPeriods(); useStore.getState().clearDashboardFilters(); }, [currentDashboard?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!wallMode || !rotating) return undefined;
    let shownSince = Date.now();
    // Moves on when the time is up, but not before a scroll has shown the whole dashboard.
    const timer = setInterval(() => {
      if (Date.now() - shownSince < rotateSeconds * 1000 || scrollPassInProgress.current) return;
      shownSince = Date.now();
      const { currentDashboard: current, currentCompany } = useStore.getState();
      const index = rotatePool.findIndex((d) => d.id === current?.id);
      const next = rotatePool[(index + 1) % rotatePool.length];
      loadDashboardData({ companyId: currentCompany?.id, dashboardId: next.id, dashboards }).catch(() => {});
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallMode, rotating, rotateSeconds, dashboards, rotatePool.map((d) => d.id).join(',')]);

  const changeRotate = (seconds) => {
    setRotateSeconds(seconds);
    try { localStorage.setItem('wallRotateSeconds', String(seconds)); } catch { /* ignore */ }
  };

  const changeScrollMode = (mode) => {
    setScrollMode(mode);
    try { localStorage.setItem('wallScrollMode', mode); } catch { /* ignore */ }
  };

  const toggleChosen = (id) => {
    const current = rotatePool.map((d) => d.id);
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    // Everything ticked is stored as "all", so a dashboard added later is included.
    const stored = next.length === dashboards.length ? [] : next;
    setChosenIds(stored);
    try { localStorage.setItem('wallDashboardIds', JSON.stringify(stored)); } catch { /* ignore */ }
  };

  // On a screen left running, the options fade so they do not cover the data. Moving the mouse brings them back.
  useEffect(() => {
    setControlsIdle(false);
    if (!wallMode || !(scrollMode !== 'off' || rotating)) return undefined;
    let timer = setTimeout(() => setControlsIdle(true), CONTROLS_FADE_MS);
    const wake = () => {
      setControlsIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setControlsIdle(true), CONTROLS_FADE_MS);
    };
    const events = ['mousemove', 'keydown', 'touchstart'];
    events.forEach((name) => window.addEventListener(name, wake));
    return () => {
      clearTimeout(timer);
      events.forEach((name) => window.removeEventListener(name, wake));
    };
  }, [wallMode, scrollMode, rotating]);

  // Keyboard shortcut (F key) to toggle fullscreen
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        // Don't trigger if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Keep the grid in step with widgets added, removed or reconfigured while
  // the same dashboard is open (the editor saves each change immediately).
  useEffect(() => {
    if (!currentDashboard?.id || renderedDashboardId !== currentDashboard.id || !isLayoutReady) return;
    const widgets = currentDashboard.widgets || [];
    setCachedWidgets(widgets);
    setLayouts((prev) => {
      const ids = new Set(widgets.map((w) => String(w.id)));
      const next = {};
      for (const bp of ['xl', 'lg', 'md', 'sm', 'xs']) {
        const kept = (prev[bp] || []).filter((item) => ids.has(item.i));
        const known = new Set(kept.map((item) => item.i));
        const missing = widgets.filter((w) => !known.has(String(w.id)));
        const wide = bp === 'xl' ? widescreenSlots(missing, kept) : new Map();
        const added = missing.map((w) => layoutItemsFor(w, wide.get(w.id))[bp]);
        next[bp] = [...kept, ...added];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDashboard?.widgets]);

  // Convert widget positions to React Grid Layout format
  useEffect(() => {
    const dashboardId = currentDashboard?.id;

    // If dashboard ID changed, reset the calculation flag
    if (currentDashboardIdRef.current !== dashboardId) {
      isCalculatingRef.current = false;
      currentDashboardIdRef.current = dashboardId;
      setIsLayoutReady(false);
      // DON'T clear renderedDashboardId - keep showing old dashboard until new one is ready
    }

    // Prevent duplicate calculations for the same dashboard
    if (isCalculatingRef.current) {
      return;
    }

    // Mark that we're calculating
    isCalculatingRef.current = true;

    setIsLayoutReady(false);

    if (!currentDashboard?.widgets) {
      setLayouts({ xl: [], lg: [], md: [], sm: [], xs: [] });
      setRenderedDashboardId(dashboardId);
      setCachedWidgets([]);
      setIsLayoutReady(true);
      isCalculatingRef.current = false;
      return;
    }

    const breakpointLayouts = { xl: [], lg: [], md: [], sm: [], xs: [] };

    const wide = widescreenSlots(currentDashboard.widgets);
    currentDashboard.widgets.forEach((widget) => {
      const items = layoutItemsFor(widget, wide.get(widget.id));
      ['xl', 'lg', 'md', 'sm', 'xs'].forEach((bp) => breakpointLayouts[bp].push(items[bp]));
    });

    setLayouts(breakpointLayouts);

    // Use requestAnimationFrame to ensure layout is fully calculated before rendering.
    // This fires after the browser completes layout computation but before paint,
    // preventing the flash of widgets in wrong positions without an arbitrary delay.
    const rafId = requestAnimationFrame(() => {
      setRenderedDashboardId(dashboardId);
      setCachedWidgets(currentDashboard?.widgets || []);
      setIsLayoutReady(true);
      isCalculatingRef.current = false;
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [currentDashboard?.id]);


  // Moves and resizes save as soon as you let go.
  const persistLayout = (layout) => {
    const next = { ...layouts, [currentBreakpoint]: layout };
    setLayouts(next);
    saveLayout(next);
  };
  const handleDragStop = (layout) => persistLayout(layout);
  const handleResizeStop = (layout) => persistLayout(layout);

  // Handle breakpoint changes
  const handleBreakpointChange = (breakpoint) => {
    setCurrentBreakpoint(breakpoint);
  };

  // Save layout changes
  const saveLayout = async (layoutsToSave = layouts) => {
    if (!currentDashboard) return;

    setIsSaving(true);
    try {
      const updatedWidgets = (currentDashboard.widgets || []).map((widget) => {
        const widgetId = widget.id.toString();
        const position = {};
        ['xl', 'lg', 'md', 'sm', 'xs'].forEach((breakpoint) => {
          const item = layoutsToSave[breakpoint]?.find((l) => l.i === widgetId);
          // A widescreen layout worked out for display is only saved once someone arranges it on a widescreen.
          if (breakpoint === 'xl' && currentBreakpoint !== 'xl' && !widget.position?.xl) return;
          if (item) position[breakpoint] = { x: Math.round(item.x), y: Math.round(item.y), w: Math.round(item.w), h: Math.round(item.h) };
        });
        return { ...widget, position };
      });

      const response = await dashboardsAPI.updateWidgetPositions(currentDashboard.id, updatedWidgets);
      if (response.success) {
        setSaveMessage('Layout saved');
        setTimeout(() => setSaveMessage(''), 2000);
      } else {
        setSaveMessage('Could not save the layout');
      }
    } catch (error) {
      console.error('[Dashboard] Error saving layout:', error);
      setSaveMessage('Could not save the layout');
    } finally {
      setIsSaving(false);
    }
  };

  // Edit mode: the dashboard becomes its own editor.
  // While editing, the grid keeps the breakpoint it had when editing began, so
  // opening a side panel (which narrows the grid) never reflows the layout or
  // saves moves to a smaller screen's layout.
  const [lockedBreakpoint, setLockedBreakpoint] = useState(null);
  useEffect(() => {
    setLockedBreakpoint(isEditMode ? currentBreakpointRef.current : null);
  }, [isEditMode]);
  const gridBreakpoints = lockedBreakpoint ? { [lockedBreakpoint]: 0 } : GRID_BREAKPOINTS;

  const enterEditMode = (openPanel = null) => {
    setEditMode(true);
    if (openPanel) editor.setPanel(openPanel);
  };
  const exitEditMode = () => {
    setEditMode(false);
    editor.setPanel(null);
  };

  if (!currentDashboard && !renderedDashboardId) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-empty">
          <h2>No dashboards yet</h2>
          {isAdmin ? (
            <>
              <p>Create one to start putting live metrics, reports and statements in front of your team.</p>
              <button type="button" className="btn-primary" onClick={() => setShowNewDashboard(true)}>Create a dashboard</button>
            </>
          ) : <p>Ask an administrator to create a dashboard for this company.</p>}
        </div>
        {showNewDashboard && <NewDashboardDialog onClose={() => setShowNewDashboard(false)} />}
      </div>
    );
  }

  const isMobile = windowWidth < 768;
  const shouldShowGrid = dataReady && isLayoutReady && renderedDashboardId === currentDashboard?.id;
  const isLoadingNewDashboard = !dataReady || (currentDashboard?.id && renderedDashboardId && renderedDashboardId !== currentDashboard?.id) || (!currentDashboard && renderedDashboardId);

  return (
    <div className={`dashboard-container ${wallMode ? 'dashboard-container--wall' : ''} ${isEditMode ? 'dashboard-container--editing' : ''} ${isEditMode && editor.panel && editor.panel.type !== 'library' ? 'dashboard-container--with-panel' : ''}`}>
      {wallMode && (
        <div className={`wall-controls ${controlsIdle && !pickerOpen ? 'wall-controls--idle' : ''}`} role="group" aria-label="Full screen display options">
          <div className="wall-controls-row">
            <label className="wall-rotate-control" title="This dashboard scrolls down and back up. Across dashboards joins the chosen dashboards into one page that keeps scrolling.">
              <span>Auto scroll</span>
              <select value={scrollMode} onChange={(e) => changeScrollMode(e.target.value)}>
                {SCROLL_OPTIONS.filter((option) => option.value !== 'across' || hasChoice).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            {hasChoice && scrollMode !== 'across' && (
              <label className="wall-rotate-control" title="Moves on to the next of the chosen dashboards">
                <span>Next dashboard</span>
                <select value={rotateSeconds} onChange={(e) => changeRotate(Number(e.target.value))}>
                  {rotateOptions(rotateSeconds).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            )}
            {hasChoice && (scrollMode === 'across' || rotateSeconds >= 10) && (
              <details className="wall-dashboards" onToggle={(e) => setPickerOpen(e.currentTarget.open)}>
                <summary>Dashboards: {rotatePool.length} of {dashboards.length}</summary>
                <div className="wall-dashboards-list">
                  {dashboards.map((d) => {
                    const ticked = rotatePool.some((p) => p.id === d.id);
                    return (
                      <label key={d.id}>
                        <input type="checkbox" checked={ticked} disabled={ticked && rotatePool.length === 1} onChange={() => toggleChosen(d.id)} />
                        {d.name}
                      </label>
                    );
                  })}
                </div>
              </details>
            )}
            {isFullscreen && (
              <button
                onClick={toggleFullscreen}
                className="fullscreen-exit-btn"
                title="Exit Fullscreen (Press F or ESC)"
              >
                Exit Fullscreen
              </button>
            )}
          </div>
          {scrollMode === 'this' && rotating && <p className="wall-controls-hint">The next dashboard waits until this one has scrolled to the bottom.</p>}
        </div>
      )}
      {wallMode && !continuousActive && (
        <div className="wall-header" data-testid="wall-header">
          <span className="wall-header-name">{currentDashboard?.name}</span>
          {nextDashboard && <span className="wall-header-rotate">Next: {nextDashboard.name}</span>}
          <span className="wall-header-clock">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      )}
      <AlertBanner compact={wallMode} />
      {!wallMode && isStale && currentDashboard && (
        <div style={{
          backgroundColor: 'rgba(255, 152, 0, 0.9)',
          color: '#000',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontWeight: '600',
          fontSize: '0.95rem',
          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
        }}>
          <span style={{ fontSize: '1.5rem' }}>⚠️</span>
          <span>Dashboard data is stale</span>
        </div>
      )}
      {!wallMode && isEditMode && (
        <div className="edit-toolbar" role="toolbar" aria-label="Dashboard editor">
          <div className="edit-toolbar-title">
            <span className="edit-toolbar-badge">Editing</span>
            <strong>{currentDashboard?.name}</strong>
            <span className="edit-toolbar-status" aria-live="polite">{isSaving ? 'Saving…' : saveMessage || 'Changes save automatically'}</span>
          </div>
          <div className="edit-toolbar-actions">
            <button type="button" className="btn-primary" onClick={() => editor.setPanel({ type: 'library' })}>+ Add widget</button>
            <button type="button" className="btn-secondary" onClick={() => editor.setPanel({ type: 'dashboard' })}>Dashboard settings</button>
            <label className="edit-toolbar-snap" title="Close gaps by moving widgets upwards">
              <input type="checkbox" checked={snapUp} onChange={(e) => { setSnapUp(e.target.checked); try { localStorage.setItem('dashboardSnapUp', e.target.checked ? '1' : '0'); } catch { /* ignore */ } }} />
              <span>Snap widgets up</span>
            </label>
            <button type="button" className="btn-primary edit-toolbar-done" onClick={exitEditMode}>Done</button>
          </div>
        </div>
      )}
      {!wallMode && !isEditMode && (
        <div className="dashboard-header" style={{
        padding: isMobile ? '0.5rem 0' : '1rem 0',
        marginBottom: isMobile ? '0.5rem' : '1rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '0.5rem' : '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 style={{ color: 'var(--kinspeed-blue)', fontSize: isMobile ? '1.5rem' : '2rem' }}>
            {currentDashboard?.name || 'Loading...'}
          </h2>
          <span style={{
            fontSize: '0.75rem',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            backgroundColor: 'var(--kinspeed-blue-hover)',
            color: 'var(--kinspeed-blue)',
            fontWeight: '600',
            textTransform: 'uppercase'
          }}>
            {currentBreakpoint === 'xl' ? 'Extra Large' :
             currentBreakpoint === 'lg' ? 'Desktop' :
             currentBreakpoint === 'md' ? 'Tablet' :
             currentBreakpoint === 'sm' ? 'Small' : 'Mobile'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          {saveMessage && (
            <span style={{
              color: saveMessage.includes('Failed') ? 'var(--accent-red)' : 'var(--accent-green)',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              fontWeight: '500'
            }}>
              {saveMessage}
            </span>
          )}
          {isAdmin && !isMobile && (
            <button
              type="button"
              onClick={() => enterEditMode()}
              className="btn-primary"
              style={{ fontSize: '1rem', padding: '0.75rem 1.5rem' }}
            >
              Edit dashboard
            </button>
          )}
          <button
            onClick={toggleFullscreen}
            className="btn-secondary"
            title="Toggle Fullscreen (Press F)"
            style={{ fontSize: isMobile ? '0.8rem' : '1rem', padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem' }}
          >
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
        </div>
      )}
      {!wallMode && !isEditMode && showFilterBar && <DashboardFilterBar dashboard={currentDashboard} />}

      {continuousActive ? (
        <ContinuousWall dashboardIds={rotatePool.map((d) => d.id)} />
      ) : shouldShowGrid || renderedDashboardId ? (
        <div ref={setGridEl} style={{ position: 'relative' }}>
          {isLoadingNewDashboard && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              backdropFilter: 'blur(2px)'
            }}>
              <div className="loading-spinner"></div>
            </div>
          )}
          <ResponsiveGridLayout
            key={renderedDashboardId}
            className="layout"
            layouts={layouts}
            breakpoints={gridBreakpoints}
            cols={{ xl: 18, lg: 12, md: 12, sm: 12, xs: 12 }}
            rowHeight={getRowHeight()}
            width={gridWidth || (isMobile ? windowWidth - 24 : windowWidth - 64)}
            isDraggable={isEditMode}
            isResizable={isEditMode}
            onBreakpointChange={handleBreakpointChange}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            draggableHandle=".widget-header"
            compactType={gridOptions.compactType}
            allowOverlap={gridOptions.allowOverlap}
            preventCollision={gridOptions.preventCollision}
            margin={isMobile ? [8, 8] : [10, 10]}
            containerPadding={isMobile ? [8, 8] : [0, 0]}
          >
            {cachedWidgets && cachedWidgets.map((widget) => {
              const selected = editor.panel?.type === 'widget' && editor.panel.widgetId === widget.id;
              return (
                <div
                  key={widget.id.toString()}
                  data-widget-id={widget.id}
                  className={[
                    isEditMode ? 'widget-frame--editing' : '',
                    selected ? 'widget-frame--selected' : '',
                    editor.highlightId === widget.id ? 'widget-frame--new' : '',
                  ].filter(Boolean).join(' ')}
                  // While editing, widgets are things to arrange, not to use: swallow
                  // clicks inside them (a drag on the title bar ends in a click on the
                  // title, which would otherwise open the report or statement page).
                  // Only the edit toolbar stays clickable.
                  onClickCapture={isEditMode ? (e) => {
                    if (e.target.closest('.widget-edit-bar')) return;
                    e.preventDefault();
                    e.stopPropagation();
                  } : undefined}
                >
                  <WidgetFactory widget={widget} />
                  {!isEditMode && !wallMode && assistantAvailable && (
                    <button
                      type="button"
                      className="widget-ai"
                      aria-label={`Ask the AI assistant about ${widgetLabel(widget)}`}
                      title="Ask the AI assistant about this widget"
                      onClick={() => openAssistant({
                        context: { dashboardId: Number(currentDashboard?.id), widgetId: widget.id, ...(widget.report?.id ? { reportId: widget.report.id } : {}) },
                        focus: { title: widgetLabel(widget), kind: widget.widget_kind || 'metric' },
                      })}
                    >
                      <AiMark size={17} />
                    </button>
                  )}
                  {isEditMode && (
                    <div className="widget-edit-bar" role="group" aria-label={`Edit ${widgetLabel(widget)}`}>
                      <span className="widget-edit-grip" title="Drag the title bar to move; drag the corner to resize" aria-hidden="true">⠿</span>
                      <button type="button" onClick={() => editor.setPanel({ type: 'widget', widgetId: widget.id })} aria-label={`Configure ${widgetLabel(widget)}`} title="Configure">⚙</button>
                      <button type="button" onClick={() => editor.duplicate(widget)} aria-label={`Duplicate ${widgetLabel(widget)}`} title="Duplicate">⧉</button>
                      <button type="button" onClick={() => { if (window.confirm(`Remove "${widgetLabel(widget)}" from this dashboard?`)) editor.remove(widget); }} aria-label={`Remove ${widgetLabel(widget)}`} title="Remove">✕</button>
                    </div>
                  )}
                </div>
              );
            })}
          </ResponsiveGridLayout>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <div className="loading-spinner"></div>
        </div>
      )}

      {shouldShowGrid && cachedWidgets && cachedWidgets.length === 0 && (
        <div className="dashboard-empty">
          <h2>This dashboard is empty</h2>
          {isAdmin ? (
            <>
              <p>Add live metrics, reports, statements or KPIs. You can drag them into place afterwards.</p>
              <button type="button" className="btn-primary" onClick={() => enterEditMode({ type: 'library' })}>+ Add your first widget</button>
            </>
          ) : <p>An administrator hasn't added anything here yet.</p>}
        </div>
      )}

      {isEditMode && editor.panel?.type === 'library' && (
        <WidgetLibrary
          widgets={currentDashboard?.widgets || []}
          onAdd={editor.add}
          onClose={() => editor.setPanel(null)}
          busyKey={editor.busyKey}
          lastAddedKey={editor.lastAddedKey}
        />
      )}
      {isEditMode && editor.panel?.type === 'widget' && (() => {
        const widget = (currentDashboard?.widgets || []).find((w) => w.id === editor.panel.widgetId);
        if (!widget) return null;
        const definition = (useStore.getState().metricDefinitions || []).find((d) => d.metric_key === widget.metric_key);
        return (
          <WidgetInspector
            widget={widget}
            saving={editor.saving}
            onSave={(config) => editor.saveWidgetConfig(widget, config)}
            onClose={() => editor.setPanel(null)}
            onRemove={() => editor.remove(widget)}
            onDuplicate={() => editor.duplicate(widget)}
            // Saved reports are edited on their own page; only older widgets without one use the builder here.
            onEditReport={widget.widget_kind === 'dataset' ? (widget.report ? () => navigate(`/reports/${widget.report.id}`) : () => editor.setReportBuilder({ widget })) : null}
            onEditDefinition={(!widget.widget_kind || widget.widget_kind === 'metric') ? () => editor.setDefinition(definition || { metric_key: widget.metric_key, display_name: widget.display_name, widget_type: widget.widget_type, config: widget.metric_config || {} }) : null}
          />
        );
      })()}
      {isEditMode && editor.panel?.type === 'dashboard' && (
        <DashboardSettingsPanel
          dashboard={currentDashboard}
          saving={editor.saving}
          onSave={editor.saveDashboard}
          onDelete={editor.deleteDashboard}
          onClose={() => editor.setPanel(null)}
        />
      )}

      {editor.reportBuilder && (
        <ReportEditor
          onClose={() => editor.setReportBuilder(null)}
          onCreate={editor.createReport}
          onUpdate={editor.updateReport}
          editingWidget={editor.reportBuilder.widget || null}
        />
      )}
      {editor.definition && (
        <AdminWidgetBuilder
          editingMetric={editor.definition.id ? editor.definition : null}
          onClose={() => editor.setDefinition(null)}
          onSuccess={async () => { editor.setDefinition(null); await editor.reload(); editor.say('Metric definition saved'); }}
        />
      )}
      {showNewDashboard && <NewDashboardDialog onClose={() => setShowNewDashboard(false)} />}

      {editor.notice && (
        <div className={`editor-toast editor-toast--${editor.notice.tone}`} role={editor.notice.tone === 'error' ? 'alert' : 'status'}>{editor.notice.text}</div>
      )}
    </div>
  );
};

export default Dashboard;
